import { useContext } from 'react'
import { AuthContext, AuthContextType } from './AuthProvider'

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

// Helper hooks
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth()
  return isAuthenticated
}

export function useUser() {
  const { user } = useAuth()
  return user
}

export function useTenantId(): string {
  const { user } = useAuth()
  return user?.tenantId || 'default-tenant'
}

export function useUserRoles(): string[] {
  const { user } = useAuth()
  return user?.roles || []
}

export function useHasRole(role: string): boolean {
  const roles = useUserRoles()
  return roles.includes(role)
}

export function useHasAnyRole(requiredRoles: string[]): boolean {
  const roles = useUserRoles()
  return requiredRoles.some((role) => roles.includes(role))
}

// FIX-05 (COL-11) — write-RBAC on the client. Roles arrive in the JWT under
// realm_access.roles (see AuthProvider.extractUser). A single user carries BOTH
// the canonical 5-role taxonomy (OWNER/ADMIN/PUBLISHER/EDITOR/VIEWER) AND the
// lowercase sgrc_* family the realm currently emits (sgrc_admin/sgrc_editor/
// sgrc_manager/sgrc_user). Treat any of these as "can write"; everyone else
// (effectively VIEWER / sgrc_user-only) is read-only and the write CTAs hide.
const WRITE_ROLES = [
  'OWNER',
  'ADMIN',
  'PUBLISHER',
  'EDITOR',
  'sgrc_admin',
  'sgrc_owner',
  'sgrc_publisher',
  'sgrc_editor',
  'sgrc_manager',
]

/** True if the current user holds any write-capable role (mirrors backend WRITE_ROLES). */
export function useCanWrite(): boolean {
  const roles = useUserRoles()
  return roles.some((r) => WRITE_ROLES.includes(r))
}

export function usePortalAccess(): string[] {
  const { user } = useAuth()
  return user?.portalAccess || []
}

export function useHasPortalAccess(portal: 'client' | 'adminclient' | 'admin'): boolean {
  const portalAccess = usePortalAccess()
  return portalAccess.includes(portal)
}
